const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://127.0.0.1:8787';
const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN ?? 'dev-token-change-me';

function getCfAccessJwt(): string | null {
  try {
    const m = document.cookie.match(/(?:^|;\s*)CF_Authorization=([^;]+)/);
    return m?.[1] ?? null;
  } catch {
    return null;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const cfJwt = getCfAccessJwt();
  if (cfJwt) {
    headers['cf-access-jwt-assertion'] = cfJwt;
  } else {
    headers['authorization'] = `Bearer ${DEV_TOKEN}`;
  }
  const init: RequestInit = { method, headers, credentials: 'include' };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(`${API_BASE}${path}`, init);
  if (!res.ok) {
    let detail: unknown;
    try { detail = await res.json(); } catch { detail = await res.text(); }
    throw new Error(`${method} ${path} → ${res.status}: ${JSON.stringify(detail)}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function download(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const cfJwt = getCfAccessJwt();
  if (cfJwt) headers['cf-access-jwt-assertion'] = cfJwt;
  else headers['authorization'] = `Bearer ${DEV_TOKEN}`;
  const res = await fetch(`${API_BASE}${path}`, { headers, credentials: 'include' });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.blob();
}

export interface SuppressorRow {
  fitment_json?: string | null;
  id: number;
  brand: string;
  model: string;
  suppressor_link: string | null;
  cover_type: 'MEDIUM' | 'HEAVY' | 'CUSTOM MEDIUM' | 'CUSTOM HEAVY';
  suppressor_diameter: number;
  suppressor_length: number;
  cover_diameter: number;
  base_sku: string;
  len_dt_covered: number | null;  sku_dt_covered: string | null;
  len_dt_exposed: number | null;  sku_dt_exposed: string | null;
  len_re_exposed: number | null;  sku_re_exposed: string | null;
  len_qda_covered: number | null; sku_qda_covered: string | null;
  len_qdb_covered: number | null; sku_qdb_covered: string | null;
  len_qda_exposed: number | null; sku_qda_exposed: string | null;
  len_qdb_exposed: number | null; sku_qdb_exposed: string | null;
  flow_through: number;
  needs_review: number;
  source_notes: string | null;
  is_active: number;
  created_at: number;
  updated_at: number;
}

export interface ColorRow {
  id: string;
  name: string;
  hex: string;
  ring_hex: string;
  display_order: number;
  is_active: number;
}

export interface VariantMappingRow {
  id: number;
  sku: string;
  color_id: string;
  platform: 'rifle' | 'handgun' | 'pcc' | '*';
  bc_product_id: number;
  bc_variant_id: number;
  bc_custom_field_diameter_id: number | null;
  bc_custom_field_length_id: number | null;
  bc_modifier_suppressor_model_id: number | null;
  bc_modifier_cover_size_id: number | null;
  bc_modifier_rear_mount_id: number | null;
  bc_modifier_rear_none_value_id: number | null;
  bc_modifier_rear_qd_small_value_id: number | null;
  bc_modifier_rear_qd_large_value_id: number | null;
  bc_modifier_front_coverage_id: number | null;
  bc_modifier_front_covered_value_id: number | null;
  bc_modifier_front_exposed_value_id: number | null;
  bc_modifier_front_flow_through_value_id: number | null;
  is_active: number;
  created_at: number;
  updated_at: number;
}

export interface Page<T> {
  total: number;
  limit: number;
  offset: number;
  items: T[];
}

export interface GoogleSheetImportIssue {
  row_index: number;
  brand: string | null;
  model: string | null;
  issues: string[];
}

export interface GoogleSheetImportResult {
  source: { csv_url: string };
  dry_run: boolean;
  approved_only: boolean;
  parsed_rows: number;
  valid_rows: number;
  eligible_rows: number;
  approved_rows: number;
  review_rows: number;
  issue_rows: number;
  database_review_rows: number;
  ambiguous_rows: number[];
  apply_offset: number;
  apply_limit: number;
  applied_rows: number;
  next_offset: number | null;
  done: boolean;
  inserted: number;
  updated: number;
  issues: GoogleSheetImportIssue[];
}

export interface CanonicalImportResult {
  dry_run: boolean;
  rows: number;
  processed_rows: number;
  inserted: number;
  updated: number;
  unchanged: number;
  review_rows: number;
  issue_rows: number;
  issues: Array<{ row: number; record_id: number | null; brand: string | null; model: string | null; issues: string[] }>;
  missing_record_ids: number[];
  ambiguous_rows: number[];
  applied: boolean;
  next_offset: number | null;
  done: boolean;
}

export interface SuppressorListQuery {
  q?: string;
  brand?: string;
  needs_review?: '0' | '1';
  is_active?: '0' | '1';
  limit?: number;
  offset?: number;
}

function buildQuery(q: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v == null || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export const api = {
  suppressors: {
    list: (q: SuppressorListQuery = {}): Promise<Page<SuppressorRow>> =>
      request('GET', `/api/admin/suppressors${buildQuery(q as Record<string, unknown>)}`),
    create: (body: Partial<SuppressorRow>) =>
      request<{ id: number }>('POST', '/api/admin/suppressors', body),
    update: (id: number, body: Partial<SuppressorRow>) =>
      request<{ id: number; updated: string[] }>('PUT', `/api/admin/suppressors/${id}`, body),
    softDelete: (id: number) =>
      request<{ id: number; is_active: 0 }>('DELETE', `/api/admin/suppressors/${id}`),
  },
  colors: {
    list: () => request<{ items: ColorRow[] }>('GET', '/api/admin/colors'),
    create: (body: Partial<ColorRow>) => request<{ id: string }>('POST', '/api/admin/colors', body),
    update: (id: string, body: Partial<ColorRow>) =>
      request<{ id: string }>('PUT', `/api/admin/colors/${id}`, body),
    softDelete: (id: string) =>
      request<{ id: string; is_active: 0 }>('DELETE', `/api/admin/colors/${id}`),
  },
  variantMappings: {
    list: (q: { sku?: string; color_id?: string; platform?: string; limit?: number; offset?: number } = {}) =>
      request<Page<VariantMappingRow>>('GET', `/api/admin/variant-mappings${buildQuery(q as Record<string, unknown>)}`),
    create: (body: Partial<VariantMappingRow>) =>
      request<{ id: number }>('POST', '/api/admin/variant-mappings', body),
    update: (id: number, body: Partial<VariantMappingRow>) =>
      request<{ id: number }>('PUT', `/api/admin/variant-mappings/${id}`, body),
    softDelete: (id: number) =>
      request<{ id: number; is_active: 0 }>('DELETE', `/api/admin/variant-mappings/${id}`),
  },
  imports: {
    googleSheet: (body: { csv_url?: string; sheet_id?: string; gid?: string; dry_run?: boolean; approved_only?: boolean; apply_offset?: number; apply_limit?: number }) =>
      request<GoogleSheetImportResult>('POST', '/api/admin/import/google-sheet', body),
    canonical: (body: { csv: string; dry_run: boolean; apply_offset?: number; apply_limit?: number }) =>
      request<CanonicalImportResult>('POST', '/api/admin/catalog/import', body),
    exportCanonical: () => download('/api/admin/catalog/export'),
  },
  brands: (): Promise<string[]> =>
    request<{ brands: string[] }>('GET', '/api/brands').then((r) => r.brands),
};
